import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Eye, EyeOff, CheckCircle, ShieldCheck } from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setDone(true);
      toast.success('Password reset successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center p-8">
        <div className="text-center">
          <h1 className="font-['Playfair_Display'] text-3xl font-bold text-white mb-4">Invalid Link</h1>
          <p className="text-white/60 mb-8">This password reset link is invalid or has expired.</p>
          <Link to="/forgot-password">
            <Button className="gold-gradient text-black font-bold" data-testid="request-new-link-btn">
              Request a New Link
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center p-8">
      <div className="w-full max-w-md">
        <Link 
          to="/login" 
          className="inline-flex items-center gap-2 text-white/60 hover:text-white mb-8 transition-colors"
          data-testid="back-to-login-link"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Login
        </Link>

        <div className="flex items-center gap-3 mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_set-roster/artifacts/4vngv49q_2ndTeamStandinsLogoWhite.png" 
            alt="2nd Team Casting" 
            className="h-36 w-auto"
          />
        </div>

        {!done ? (
          <>
            <h1 className="font-['Playfair_Display'] text-3xl font-bold text-white mb-2">
              Reset Password
            </h1>
            <p className="text-white/60 mb-8">
              Enter your new password below.
            </p>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="password" className="text-white/80">New Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="At least 8 characters"
                    required
                    minLength={8}
                    className="bg-[#0A0A0A] border-white/10 text-white placeholder:text-white/30 focus:border-[#D4AF37] h-12 pr-12"
                    data-testid="reset-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-white/50 hover:text-white"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm" className="text-white/80">Confirm Password</Label>
                <Input
                  id="confirm"
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter your password"
                  required
                  className="bg-[#0A0A0A] border-white/10 text-white placeholder:text-white/30 focus:border-[#D4AF37] h-12"
                  data-testid="reset-confirm-input"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full gold-gradient text-black font-bold h-12 uppercase tracking-wider hover:brightness-110 transition-all disabled:opacity-50"
                data-testid="reset-submit-btn"
              >
                <ShieldCheck className="w-4 h-4 mr-2" />
                {loading ? 'Resetting...' : 'Reset Password'}
              </Button>
            </form>
          </>
        ) : (
          <div className="text-center">
            <div className="w-16 h-16 rounded-full bg-[#10B981]/20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-[#10B981]" />
            </div>
            <h1 className="font-['Playfair_Display'] text-3xl font-bold text-white mb-4">
              Password Reset
            </h1>
            <p className="text-white/60 mb-8">
              Your password has been updated. You can now log in with your new password.
            </p>
            <Button
              onClick={() => navigate('/login')}
              className="gold-gradient text-black font-bold"
              data-testid="go-to-login-btn"
            >
              Go to Login
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResetPasswordPage;
